import { jDataView } from './jDataView';

export interface ID3TagData {
    title?: string;
    artist?: string;
    album?: string;
    year?: string;
    trackNumber?: number;
    albumArt?: {
        data: ArrayBuffer;
        mimeType: string;
        description?: string;
        pictureType?: number;
    };
}

/**
 * Read ID3v2 header and determine tag size
 */
function readID3v2Header(dv: any): { version: number; size: number } | null {
    // Check for ID3v2 header at the beginning of file
    if (dv.getString(3, 0) !== 'ID3') {
        return null;
    }

    const majorVersion = dv.getUint8(3);
    dv.getUint8(4); // minor version (unused)
    dv.getUint8(5); // flags (unused)

    // Read synchsafe integer for tag size
    const sizeBytes = [
        dv.getUint8(6),
        dv.getUint8(7),
        dv.getUint8(8),
        dv.getUint8(9)
    ];

    // Convert synchsafe integer to regular integer
    const size = (sizeBytes[0] << 21) | (sizeBytes[1] << 14) | (sizeBytes[2] << 7) | sizeBytes[3];

    return {
        version: majorVersion,
        size: size + 10 // Add header size
    };
}

/**
 * Read ID3v2 frame header
 */
function readFrameHeader(dv: any, offset: number, version: number): { id: string; size: number; flags: number } | null {
    if (offset + 10 > dv.byteLength) {
        return null;
    }

    const id = dv.getString(4, offset);

    // Check for padding (null bytes)
    if (id === '\0\0\0\0') {
        return null;
    }

    let size: number;
    if (version === 4) {
        // ID3v2.4 uses synchsafe integers for frame size
        const sizeBytes = [
            dv.getUint8(offset + 4),
            dv.getUint8(offset + 5),
            dv.getUint8(offset + 6),
            dv.getUint8(offset + 7)
        ];
        size = (sizeBytes[0] << 21) | (sizeBytes[1] << 14) | (sizeBytes[2] << 7) | sizeBytes[3];
    } else {
        // ID3v2.3 uses regular 32-bit integer
        size = dv.getUint32(offset + 4);
    }

    const flags = dv.getUint16(offset + 8);

    return { id, size, flags };
}

/**
 * Parse APIC frame (album cover art) with proper encoding support
 */
function parseAPICFrame(dv: any, offset: number, size: number): ID3TagData['albumArt'] {
    try {
        let pos = offset;

        // Text encoding (1 byte)
        const encoding = dv.getUint8(pos);
        pos++;

        // MIME type (null-terminated Latin-1 string)
        let mimeType = '';
        while (pos < offset + size && dv.getUint8(pos) !== 0) {
            mimeType += String.fromCharCode(dv.getUint8(pos));
            pos++;
        }
        pos++; // Skip null terminator

        // Picture type (1 byte)
        const pictureType = dv.getUint8(pos);
        pos++;

        // Description (null-terminated string, encoding depends on encoding byte)
        let description = '';
        const descStart = pos;

        // Find end of description based on encoding
        let descEnd = descStart;
        if (encoding === 1) { // UTF-16
            // Look for double null (0000)
            while (descEnd < offset + size - 1) {
                if (dv.getUint8(descEnd) === 0 && dv.getUint8(descEnd + 1) === 0) {
                    break;
                }
                descEnd += 2;
            }
            descEnd += 2; // Skip double null
        } else {
            // Look for single null
            while (descEnd < offset + size && dv.getUint8(descEnd) !== 0) {
                descEnd++;
            }
            descEnd++; // Skip null terminator
        }

        // Parse description if present
        if (descEnd > descStart + 1) {
            const descSize = descEnd - descStart - (encoding === 1 ? 2 : 1);
            if (descSize > 0) {
                const descData = new Uint8Array(descSize);
                for (let i = 0; i < descSize; i++) {
                    descData[i] = dv.getUint8(descStart + i);
                }

                switch (encoding) {
                    case 0: // ISO-8859-1
                        description = String.fromCharCode(...descData);
                        break;
                    case 1: // UTF-16
                        description = decodeUTF16(descData);
                        break;
                    case 2: // UTF-16BE
                        description = decodeUTF16BE(descData);
                        break;
                    case 3: // UTF-8
                        description = decodeUTF8(descData);
                        break;
                    default:
                        description = String.fromCharCode(...descData);
                        break;
                }
            }
        }

        pos = descEnd;

        // Picture data (rest of the frame)
        const dataSize = size - (pos - offset);
        const data = new ArrayBuffer(dataSize);
        const dataView = new Uint8Array(data);
        for (let i = 0; i < dataSize; i++) {
            dataView[i] = dv.getUint8(pos + i);
        }

        return {
            data,
            mimeType,
            description: description.trim() || undefined,
            pictureType
        };
    } catch (error) {
        console.warn('Error parsing APIC frame:', error);
        return undefined;
    }
}/**
 * Parse text frame (TIT2, TPE1, TALB, TYER, etc.) with proper encoding support
 */
function parseTextFrame(dv: any, offset: number, size: number): string | undefined {
    try {
        if (size < 2) return undefined;

        // Text encoding (1 byte)
        const encoding = dv.getUint8(offset);

        // Extract text data bytes
        const textData = new Uint8Array(size - 1);
        for (let i = 0; i < size - 1; i++) {
            textData[i] = dv.getUint8(offset + 1 + i);
        }

        let text: string;

        switch (encoding) {
            case 0: // ISO-8859-1 (Latin-1)
                text = String.fromCharCode(...textData);
                break;

            case 1: // UTF-16 with BOM
                text = decodeUTF16(textData);
                break;

            case 2: // UTF-16BE (Big Endian, no BOM)
                text = decodeUTF16BE(textData);
                break;

            case 3: // UTF-8
                text = decodeUTF8(textData);
                break;

            default:
                // Fallback to Latin-1
                text = String.fromCharCode(...textData);
                break;
        }

        // Remove null terminators and trim
        return text.replace(/\0/g, '').trim() || undefined;
    } catch (error) {
        console.warn('Error parsing text frame:', error);
        return undefined;
    }
}

function parseTrackNumber(text?: string): number | undefined {
    if (!text) {
        return undefined;
    }

    const match = text.match(/^(\d{1,3})/);
    if (!match) {
        return undefined;
    }

    const track = parseInt(match[1], 10);
    if (Number.isNaN(track) || track <= 0) {
        return undefined;
    }

    return track;
}

/**
 * Decode UTF-16 with BOM detection
 */
function decodeUTF16(data: Uint8Array): string {
    if (data.length < 2) return '';

    // Check for BOM
    const bom = (data[0] << 8) | data[1];
    let littleEndian = false;
    let startIndex = 0;

    if (bom === 0xFFFE) {
        // UTF-16 Little Endian BOM
        littleEndian = true;
        startIndex = 2;
    } else if (bom === 0xFEFF) {
        // UTF-16 Big Endian BOM
        littleEndian = false;
        startIndex = 2;
    } else {
        // No BOM, assume little endian (common case)
        littleEndian = true;
        startIndex = 0;
    }

    // Convert to UTF-16 code units
    const codeUnits: number[] = [];
    for (let i = startIndex; i < data.length - 1; i += 2) {
        let codeUnit: number;
        if (littleEndian) {
            codeUnit = data[i] | (data[i + 1] << 8);
        } else {
            codeUnit = (data[i] << 8) | data[i + 1];
        }

        if (codeUnit !== 0) { // Skip null terminators
            codeUnits.push(codeUnit);
        }
    }

    return String.fromCharCode(...codeUnits);
}

/**
 * Decode UTF-16 Big Endian (no BOM)
 */
function decodeUTF16BE(data: Uint8Array): string {
    const codeUnits: number[] = [];
    for (let i = 0; i < data.length - 1; i += 2) {
        const codeUnit = (data[i] << 8) | data[i + 1];
        if (codeUnit !== 0) {
            codeUnits.push(codeUnit);
        }
    }
    return String.fromCharCode(...codeUnits);
}

/**
 * Decode UTF-8
 */
function decodeUTF8(data: Uint8Array): string {
    try {
        // Use built-in TextDecoder for UTF-8
        const decoder = new TextDecoder('utf-8');
        return decoder.decode(data);
    } catch (error) {
        // Fallback to manual decoding
        console.warn('UTF-8 decode error, falling back to Latin-1:', error);
        return String.fromCharCode(...data);
    }
}/**
 * Read ID3v2 tags from an MP3 file
 */
function readID3v2Tags(dv: any): ID3TagData {
    const header = readID3v2Header(dv);
    if (!header) {
        return {};
    }

    const tags: ID3TagData = {};
    let offset = 10; // Skip header

    while (offset < header.size) {
        const frameHeader = readFrameHeader(dv, offset, header.version);
        if (!frameHeader || frameHeader.size === 0) {
            break;
        }

        const frameOffset = offset + 10; // Frame header is 10 bytes

        switch (frameHeader.id) {
            case 'TIT2': // Title
                tags.title = parseTextFrame(dv, frameOffset, frameHeader.size);
                break;
            case 'TPE1': // Artist
                tags.artist = parseTextFrame(dv, frameOffset, frameHeader.size);
                break;
            case 'TALB': // Album
                tags.album = parseTextFrame(dv, frameOffset, frameHeader.size);
                break;
            case 'TYER': // Year (ID3v2.3)
            case 'TDRC': // Recording date (ID3v2.4)
                tags.year = parseTextFrame(dv, frameOffset, frameHeader.size);
                break;
            case 'TRCK': { // Track number
                const trackText = parseTextFrame(dv, frameOffset, frameHeader.size);
                const trackNumber = parseTrackNumber(trackText);
                if (trackNumber !== undefined) {
                    tags.trackNumber = trackNumber;
                }
                break;
            }
            case 'APIC': // Album cover art
                if (!tags.albumArt) { // Take first image found
                    tags.albumArt = parseAPICFrame(dv, frameOffset, frameHeader.size);
                }
                break;
        }

        offset += 10 + frameHeader.size;
    }

    return tags;
}

/**
 * Read ID3v1 tags from an MP3 file
 * Based on the implementation in playbackStore.ts
 */
function readID3v1Tags(dv: any): ID3TagData {
    // "TAG" starts at byte -128 from EOF.
    // See http://en.wikipedia.org/wiki/ID3
    if (dv.getString(3, dv.byteLength - 128) === 'TAG') {
        const title = dv.getString(30, dv.tell()).trim();
        const artist = dv.getString(30, dv.tell()).trim();
        const album = dv.getString(30, dv.tell()).trim();
        const year = dv.getString(4, dv.tell()).trim();

        const commentBytes = dv.getBytes(30, dv.tell());
        let trackNumber: number | undefined;

        if (commentBytes.length === 30) {
            const trackIndicator = commentBytes[28];
            const potentialTrack = commentBytes[29];
            if (trackIndicator === 0 && potentialTrack > 0) {
                trackNumber = potentialTrack;
            }
        }

        return {
            title: title || undefined,
            artist: artist || undefined,
            album: album || undefined,
            year: year || undefined,
            trackNumber
        };
    }

    return {};
}

/**
 * Read ID3 tags (both v1 and v2) from an MP3 file
 * ID3v2 takes precedence over ID3v1 when both are present
 */
export async function readID3Tags(file: File): Promise<ID3TagData> {
    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = (_e: ProgressEvent<FileReader>) => {
            try {
                if (!reader.result) {
                    resolve({});
                    return;
                }

                // @ts-ignore - jDataView expects ArrayBuffer
                const dv = new jDataView(reader.result);

                // Try to read ID3v2 tags first (they have priority and include album art)
                const id3v2Tags = readID3v2Tags(dv);

                // Try to read ID3v1 tags as fallback
                const id3v1Tags = readID3v1Tags(dv);

                // Merge tags with ID3v2 taking precedence
                const mergedTags: ID3TagData = {
                    title: id3v2Tags.title || id3v1Tags.title,
                    artist: id3v2Tags.artist || id3v1Tags.artist,
                    album: id3v2Tags.album || id3v1Tags.album,
                    year: id3v2Tags.year || id3v1Tags.year,
                    trackNumber: id3v2Tags.trackNumber || id3v1Tags.trackNumber,
                    albumArt: id3v2Tags.albumArt // Only available in ID3v2
                };

                resolve(mergedTags);
            } catch (error) {
                console.warn('Error reading ID3 tags:', error);
                resolve({});
            }
        };

        reader.onerror = () => {
            console.warn('Error reading file for ID3 tags');
            resolve({});
        };

        reader.readAsArrayBuffer(file);
    });
}

/**
 * Read ID3 tags from a FileSystemFileHandle
 */
export async function readID3TagsFromHandle(fileHandle: FileSystemFileHandle): Promise<ID3TagData> {
    try {
        const file = await fileHandle.getFile();
        return await readID3Tags(file);
    } catch (error) {
        console.warn('Error reading ID3 tags from file handle:', error);
        return {};
    }
}